import { useEffect, useState } from "react";
import { Security } from "@mui/icons-material";
import { Box, Typography, Link } from "@mui/material";
import SelectBoxGrid from "./SelectBoxGrid";
import { useCustomStripe } from "../common/Providers/CustomStripeProvider/UseCustomStripe";
import { useVehicleReservation } from "../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useStoreDetails } from "../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { updateReservationPre } from "../api/Product";

const BodhiProtect = () => {
  const { amount, clientSecret, stripePromise } = useCustomStripe();
  const { storeDetails } = useStoreDetails();
  const { VehicleReservationMain, setVehicleReservationValue } = useVehicleReservation();

  const [protectionSelected, setProtectionSelected] = useState<string>("1");

  useEffect(() => {
    if (!storeDetails.is_bodhi_protect_enabled) return;
    const defaultVal = !!storeDetails.bodhi_protect_default;
    setProtectionSelected(defaultVal ? "1" : "0");

    const percentage = parseFloat(storeDetails.bodhi_protect_percentage || "0");
    const upcharge = VehicleReservationMain.upcharge || 0;
    const baseAmount = VehicleReservationMain.prices.subtotal + upcharge;
    const rawPrice = baseAmount * (isNaN(percentage) ? 0 : percentage / 100);
    const defaultProtectionPrice = parseFloat(rawPrice.toFixed(2));

    if (defaultVal) {
      setVehicleReservationValue("bodhi_protect_opted_out", false);
      setVehicleReservationValue("bodhi_protect_price", defaultProtectionPrice);
    } else {
      setVehicleReservationValue("bodhi_protect_opted_out", true);
      setVehicleReservationValue("bodhi_protect_price", 0);
    }
  }, [storeDetails.bodhi_protect_default, storeDetails.is_bodhi_protect_enabled, VehicleReservationMain.prices.subtotal, VehicleReservationMain.upcharge]);

  const upcharge = VehicleReservationMain.upcharge || 0;
  const baseAmount = VehicleReservationMain.prices.subtotal + upcharge;
  const protectionPrice = (baseAmount * (parseFloat(storeDetails.bodhi_protect_percentage) / 100)).toFixed(2);

  const PROTECTION_OPTIONS = [
    {
      id: "1",
      label: `Yes, protect my booking (Add $${protectionPrice})`,
    },
    {
      id: "0",
      label: "No thanks, I'll take the risk",
    },
  ];

  if (!storeDetails.is_bodhi_protect_enabled) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            padding: "8px",
            borderRadius: "50%",
            backgroundColor: "#e3f2fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.5,
          }}
        >
          <Security sx={{ width: 20, height: 20 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Protect your booking - Make it refundable!
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1.5, color: "#555" }}>
        Upgrade your booking to get a fast, full refund if you can&apos;t attend
        due to one of the many covered reasons listed below.
      </Typography>

      <Box sx={{ mb: 2 }}>
        {[
          "Accident, Illness & Pre-Existing conditions",
          "Transport disruptions",
          "Family or home emergency",
          "And many more!",
        ].map((item) => (
          <Box
            key={item}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
                color: "#fff",
                fontSize: 14,
              }}
            >
              ✓
            </Box>
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>

      <SelectBoxGrid
        title=""
        options={PROTECTION_OPTIONS}
        selectedId={protectionSelected}
        cardContainerSx={{ padding: 0 }}
        columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        onSelect={(id) => {
          setProtectionSelected(id);
          if (id === "1") {
            setVehicleReservationValue("bodhi_protect_opted_out", false);
            setVehicleReservationValue("bodhi_protect_price", parseFloat(protectionPrice));
          } else {
            setVehicleReservationValue("bodhi_protect_opted_out", true);
            setVehicleReservationValue("bodhi_protect_price", 0);
          }
          const preId = localStorage.getItem('preId');
          if (preId) updateReservationPre({ id: preId, bodhi_protect_opted_out: id !== "1", bodhi_protect_price: id === "1" ? parseFloat(protectionPrice) : 0 });
        }}
      />

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.5, color: "#777" }}
      >
        By selecting "Yes, protect my booking" you are agreeing to all{" "}
        <Link href="/termsandconditions" target="_blank" underline="always">
          Terms &amp; Conditions
        </Link>
        , and you may receive up to a 100% refund on today&apos;s purchase. May
        not apply to pricing modifications made in the future.
      </Typography>
    </Box>
  );
};

export default BodhiProtect;
